import React from "react";
import { Link } from "react-router-dom";
import LanguageSelector from "./LanguageSelector";
import { trackEvent } from "../utils/metaPixel";
import { trackButtonClick } from "../utils/analytics";
import {
  FiHome,
  FiInfo,
  FiStar,
  FiDollarSign,
  FiFileText,
  FiPhone,
  FiGlobe,
  FiMessageCircle,
  FiCpu,
  FiClock,
  FiBarChart2,
  FiHelpCircle,
  FiImage,
  FiSettings,
  FiLayers,
  FiTarget,
  FiTrendingUp,
  FiShield,
  FiMapPin,
} from "react-icons/fi";

const LandingHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const handleTryNow = React.useCallback(() => {
    // Track the click event with Meta Pixel
    trackEvent("TryNowClick", { button_location: "header" });

    // Also track with Firebase Analytics
    trackButtonClick("try_now_header");

    const userAccessToken = localStorage.getItem("userAccessToken");
    if (userAccessToken) {
      window.location.href = "/ai-price";
    } else {
      window.location.href = "/login?returnUrl=/ai-price";
    }
  }, []);

  const menuItems = [
    { name: "Trang Chủ", icon: <FiHome className="mr-2" />, href: "/" },
    { name: "Tính Năng", icon: <FiStar className="mr-2" />, href: "#features" },
    {
      name: "Bảng Giá",
      icon: <FiDollarSign className="mr-2" />,
      href: "#pricing",
    },
    {
      name: "Khách Hàng",
      icon: <FiStar className="mr-2" />,
      href: "#testimonials",
    },
    { name: "Liên Hệ", icon: <FiPhone className="mr-2" />, href: "#contact" },
  ];

  return (
    <header className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white py-3 shadow-2xl sticky top-0 z-50 backdrop-blur-sm border-b border-blue-800/30">
      <div className="container mx-auto flex justify-between items-center px-4 lg:px-8">
        <Link
          to="/"
          className="flex items-center gap-4 font-bold text-2xl group"
        >
          <div className="relative">
            <img
              src={require("../assets/images/aiprice-logo.png")}
              alt="AIPrice Logo"
              className="h-14 w-auto border-2 border-blue-400 rounded-xl shadow-lg group-hover:border-yellow-400 transition-all duration-300"
              loading="lazy"
            />
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur opacity-20 group-hover:opacity-40 transition duration-300"></div>
          </div>
          <div className="flex flex-col">
            <span className="text-2xl xl:text-3xl font-bold bg-gradient-to-r from-blue-400 to-blue-200 bg-clip-text text-transparent">
              AIPrice
            </span>
            <span className="text-xs text-blue-300 font-medium -mt-1">
              Định giá BĐS thông minh
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          <nav>
            <ul className="flex space-x-6 xl:space-x-8">
              {menuItems.map((item, index) => (
                <li key={index}>
                  <a
                    href={item.href}
                    className="hover:text-yellow-300 transition-all duration-300 flex items-center text-sm xl:text-base font-medium group relative"
                  >
                    <span className="relative z-10">{item.name}</span>
                    <div className="absolute inset-0 bg-blue-700/50 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 -z-0 scale-95 group-hover:scale-100"></div>
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex items-center gap-4">
            <LanguageSelector key="desktop-lang-selector" isInHeader={true} />
            <button
              onClick={handleTryNow}
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-6 py-3 rounded-full font-bold hover:from-yellow-300 hover:to-yellow-400 transition-all duration-300 text-sm xl:text-base shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 border border-yellow-300"
            >
              <span className="flex items-center gap-2">
                <FiTrendingUp className="text-sm" />
                Định giá ngay
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center gap-4">
          <LanguageSelector key="mobile-lang-selector" isInHeader={true} />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-white focus:outline-none bg-blue-800/50 p-2 rounded-lg border border-blue-700/50"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-gradient-to-b from-blue-900 to-slate-900 mt-2 py-6 px-4 border-t border-blue-800/50">
          <nav>
            <ul className="space-y-4">
              {menuItems.map((item, index) => (
                <li key={index}>
                  <a
                    href={item.href}
                    className="hover:text-yellow-300 transition-all duration-300 flex items-center text-base font-medium py-3 px-4 rounded-lg hover:bg-blue-800/50 border border-transparent hover:border-blue-700/50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.icon}
                    {item.name}
                  </a>
                </li>
              ))}
              <li className="pt-4 mt-4 border-t border-blue-800/50">
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleTryNow();
                  }}
                  className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-6 py-3 rounded-full font-bold hover:from-yellow-300 hover:to-yellow-400 transition-all duration-300 text-base shadow-lg"
                >
                  <span className="flex items-center justify-center gap-2">
                    <FiTrendingUp className="text-sm" />
                    Định giá ngay
                  </span>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
};

export default LandingHeader;
